import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../utils/permissions';
import { CHART_CHANNELS, scalesFor, seriesFor } from '../utils/telemetrySeries';
import LinkedSystemsPanel from '../components/bioreactor/LinkedSystemsPanel';

/*
 * Bioreactor Monitor -- a read-only window onto a paired MABP rig.
 *
 * What it shows: rig status (simulated or real, SAFE_HOLD, who holds the
 * control lease, probe calibration), the last hours of pH / dissolved oxygen /
 * temperature, active alarms and batches with the culture they came from.
 *
 * What it will never do: write a setpoint, dose, take the lease or trigger
 * E-STOP. Those stay in the MABP cockpit, which "Open cockpit" links to. The
 * one action here records a batch with its PITRIOT lineage.
 *
 * Every call goes to PITRIOT's own /links routes (routes/lab_links.py); the
 * browser never talks to the rig and never sees the link token.
 *
 * It is also where every lab system is paired and listed, MYCELIUM servers as
 * well as rigs (components/bioreactor/LinkedSystemsPanel.js, the old /links
 * page). Links exist on the desktop/LAN installation only: in the cloud
 * GET /links answers 501, and the page says so instead.
 */

// routes/lab_links.py takes one scope for the whole module, bioreactor:access
// (a system admin or a collection manager, 2026-10-13): whoever opens the page
// pairs and unpairs systems and records batches; nobody else reaches it.
const WINDOW_HOURS = 6;
const RIG_POLL_MS = 15000;
const TELEMETRY_POLL_MS = 30000;

const errorText = (e) => e?.response?.data?.error || e?.message || String(e);

function Chip({ tone, children }) {
  const colours = {
    good: ['var(--c-primary-soft-6)', 'var(--c-success-fg)'],
    warn: ['var(--c-warning-soft)', 'var(--c-warning-fg)'],
    bad: ['var(--c-danger-soft-3)', 'var(--c-danger-fg)'],
    neutral: ['var(--c-bg-muted)', 'var(--c-text-fg)'],
  }[tone] || [];
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                   background: colours[0], color: colours[1], whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Card({ title, children, actions }) {
  return (
    <section style={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10,
                      padding: 16, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function ChannelChart({ channel, label, points, startMs, endMs, t }) {
  const width = 520;
  const height = 150;
  const series = useMemo(() => seriesFor(points, channel), [points, channel]);
  const { x, y, lo, hi } = scalesFor(series, startMs, endMs, width, height, 12);
  return (
    <figure style={{ margin: 0 }} data-testid={`chart-${channel}`}>
      <figcaption style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <strong>{label}</strong>
        <span>
          {series.latest
            ? `${series.latest.value.toFixed(2)} ${series.unit}`
            : t('chart.noGoodData')}
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block',
                                                       background: 'var(--c-bg-subtle)', borderRadius: 6 }}
           role="img" aria-label={label}>
        {series.segments.map((seg, i) => (
          <polyline key={i} fill="none" stroke="var(--c-primary)" strokeWidth="2"
                    points={seg.map(p => `${x(p.t).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')} />
        ))}
        {series.flagged.map((p, i) => (
          <circle key={`f${i}`} cx={x(p.t)} cy={height - 6} r="3" fill="none" stroke="var(--c-danger)">
            <title>{t('chart.flagged', { quality: p.quality })}</title>
          </circle>
        ))}
        {series.segments.length > 0 && (
          <>
            <text x="4" y="12" fontSize="10" fill="currentColor">{hi.toFixed(2)}</text>
            <text x="4" y={height - 16} fontSize="10" fill="currentColor">{lo.toFixed(2)}</text>
          </>
        )}
      </svg>
      {series.flagged.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginTop: 2 }}>
          {t('chart.flaggedCount', { count: series.flagged.length })}
        </div>
      )}
    </figure>
  );
}

function RecordBatchForm({ linkId, onRecorded, t }) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const submit = async () => {
    setBusy(true); setMessage(null);
    try {
      const found = await api.get('/lookup', { params: { code } });
      const kind = found.data?.type;
      if (kind !== 'slant' && kind !== 'plate') {
        setMessage({ error: true, text: t('record.notCulture') });
        return;
      }
      const key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString();
      await api.post(`/links/${linkId}/batches`,
        { label, inoculum: { kind, id: found.data.id } },
        { headers: { 'Idempotency-Key': key } });
      setMessage({ error: false, text: t('record.done') });
      setCode(''); setLabel('');
      onRecorded();
    } catch (e) {
      setMessage({ error: true, text: errorText(e) });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <label style={{ fontSize: 13 }}>
        {t('record.code')}
        <input id="bioreactor-inoculum-code" value={code} onChange={e => setCode(e.target.value)} title={t('tip.inoculumCode')}
               style={{ display: 'block', marginTop: 4 }} dir="ltr" />
      </label>
      <label style={{ fontSize: 13 }}>
        {t('record.label')}
        <input id="bioreactor-batch-label" value={label} onChange={e => setLabel(e.target.value)} title={t('tip.batchLabel')}
               style={{ display: 'block', marginTop: 4 }} />
      </label>
      <button type="button" onClick={submit} disabled={busy || !code || !label} title={t('tip.recordBatch')}>{t('record.submit')}</button>
      {message && (
        <div role={message.error ? 'alert' : 'status'} style={{ flexBasis: '100%', fontSize: 13,
             color: message.error ? 'var(--c-danger-2-fg)' : 'var(--c-success-fg)' }}>
          {message.text}
        </div>
      )}
    </div>
  );
}

const CAL_TONE = { valid: 'good', stale: 'warn', rejected: 'bad', missing: 'bad' };

export default function BioreactorMonitor() {
  const { t } = useTranslation('bioreactor');
  const { user } = useAuth();
  // bioreactor:read sees the paired systems and their telemetry;
  // bioreactor:access -- the same people, not the read-only executive --
  // pairs, records batches and takes stills.
  const canRead = can(user, 'bioreactor:read');
  const canAccess = can(user, 'bioreactor:access');
  const isManager = canAccess;
  const canRecord = canAccess;

  const [links, setLinks] = useState(null); // the MABP rigs
  const [allLinks, setAllLinks] = useState(null); // every paired system
  const [desktopOnly, setDesktopOnly] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rig, setRig] = useState(null);
  const [rigError, setRigError] = useState(null);
  const [telemetry, setTelemetry] = useState({ points: [], startMs: 0, endMs: 1 });
  const [alarms, setAlarms] = useState([]);
  const [batches, setBatches] = useState([]);

  const loadLinks = useCallback(() => {
    api.get('/links')
      .then(res => {
        const all = res.data?.links || [];
        const rigs = all.filter(l => l.kind === 'mabp');
        setLinkError(null);
        setAllLinks(all);
        setLinks(rigs);
        setSelected(current => (rigs.find(l => l.link_id === current) ? current : rigs[0]?.link_id || null));
      })
      .catch(e => {
        if (e?.response?.status === 501) {
          setDesktopOnly(true);
          setLinks([]);
        } else {
          setLinkError(errorText(e));
        }
      });
  }, []);

  useEffect(() => { if (canRead) loadLinks(); }, [loadLinks, canRead]);

  const loadRig = useCallback(() => {
    if (!selected) return;
    api.get(`/links/${selected}/rig`)
      .then(res => { setRig(res.data); setRigError(null); })
      // The last good status is withdrawn: a rig that stopped answering must
      // not keep showing readings that look current.
      .catch(e => { setRig(null); setRigError({ text: errorText(e), state: e?.response?.data?.link_state }); });
    api.get(`/links/${selected}/alarms`)
      .then(res => setAlarms(res.data?.items || []))
      .catch(() => {});
  }, [selected]);

  const loadTelemetry = useCallback(() => {
    if (!selected) return;
    const endMs = Date.now();
    const startMs = endMs - WINDOW_HOURS * 3600 * 1000;
    api.get(`/links/${selected}/telemetry`, { params: {
      channels: CHART_CHANNELS.join(','),
      start: new Date(startMs).toISOString(),
      end: new Date(endMs).toISOString(),
    } })
      .then(res => setTelemetry({ points: res.data?.points || [], startMs, endMs }))
      .catch(() => {});
  }, [selected]);

  const loadBatches = useCallback(() => {
    if (!selected) return;
    api.get(`/links/${selected}/batches`, { params: { limit: 25 } })
      .then(res => setBatches(res.data?.items || []))
      .catch(() => {});
  }, [selected]);

  useEffect(() => {
    // Another rig: nothing of the previous one's stays on screen meanwhile.
    setRig(null); setRigError(null); setAlarms([]); setBatches([]);
    setTelemetry({ points: [], startMs: 0, endMs: 1 });
    if (!selected) return undefined;
    loadRig(); loadTelemetry(); loadBatches();
    const rigTimer = setInterval(loadRig, RIG_POLL_MS);
    const telemetryTimer = setInterval(loadTelemetry, TELEMETRY_POLL_MS);
    return () => { clearInterval(rigTimer); clearInterval(telemetryTimer); };
  }, [selected, loadRig, loadTelemetry, loadBatches]);

  const link = links?.find(l => l.link_id === selected);

  // A deep link from someone without bioreactor:read: said, not loaded.
  if (!canRead) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="bioreactor-restricted">{t('accessRestricted')}</p>;
  }
  if (links === null && !linkError) return <p>{t('loading')}</p>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 16 }}>
      <header style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>{t('title')}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('readOnly')}</p>
        </div>
        {link && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {links.length > 1 && (
              <select id="bioreactor-link" value={selected} onChange={e => setSelected(e.target.value)} title={t('tip.rig')}>
                {links.map(l => <option key={l.link_id} value={l.link_id}>{l.label}</option>)}
              </select>
            )}
            <a href={link.cockpit_url} target="_blank" rel="noreferrer" title={t('tip.cockpit')}>{t('openCockpit')}</a>
          </div>
        )}
      </header>

      {linkError && <div role="alert">{linkError}</div>}

      {desktopOnly && (
        <Card title={t('links.title')}>
          <p style={{ margin: 0 }} data-testid="links-desktop-only">{t('links.desktopOnly')}</p>
        </Card>
      )}

      {!desktopOnly && links && links.length === 0 && (
        <Card title={t('empty.title')}><p style={{ margin: 0 }}>{t(isManager ? 'empty.bodyManager' : 'empty.body')}</p></Card>
      )}

      {link && rigError && (
        <div role="alert" style={{ padding: 12, borderRadius: 8, background: 'var(--c-danger-soft-3)' }}>
          <strong>{t(`state.${rigError.state || 'unreachable'}`)}</strong> — {rigError.text}
        </div>
      )}

      {link && rig && (
        <>
          {rig.simulated === true && (
            <div role="status" data-testid="source-simulated" style={{ padding: 10, borderRadius: 8, background: 'var(--c-warning-soft)',
                                        color: 'var(--c-warning-fg)', fontWeight: 600 }}>
              {t('simulated')}
            </div>
          )}
          {/* A positive statement for real hardware, not just the absence of
              the warning -- and an unreported source is said to be unknown. */}
          {rig.simulated === false && (
            <div role="status" data-testid="source-live" style={{ padding: 10, borderRadius: 8, background: 'var(--c-success-soft)',
                                        color: 'var(--c-success-2-fg)', fontWeight: 600 }}>
              ● {t('live', { site: rig.site })}
            </div>
          )}
          {rig.simulated !== true && rig.simulated !== false && (
            <div role="status" data-testid="source-unknown" style={{ padding: 10, borderRadius: 8, background: 'var(--c-warning-soft)',
                                        color: 'var(--c-warning-fg)' }}>
              {t('sourceUnknown')}
            </div>
          )}
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Card title={t('status.title')}>
              <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                <div>{t('status.site')}: <strong>{rig.site}</strong></div>
                <div>{t('status.safeHold')}: {rig.safe_hold
                  ? <Chip tone="bad">{t('status.safeHoldOn')}</Chip>
                  : <Chip tone="good">{t('status.safeHoldOff')}</Chip>}</div>
                <div>{t('status.lease')}: {rig.lease?.held
                  ? <Chip tone="warn">{t('status.leaseHeld', { holder: rig.lease.holder_label })}</Chip>
                  : <Chip tone="neutral">{t('status.leaseFree')}</Chip>}</div>
              </div>
            </Card>
            <Card title={t('calibration.title')}>
              <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                {Object.entries(rig.calibration || {}).map(([sensor, state]) => (
                  <div key={sensor}>{t(`channels.${sensor}`)}: <Chip tone={CAL_TONE[state] || 'bad'}>{t(`calibration.${state}`)}</Chip></div>
                ))}
              </div>
            </Card>
            <Card title={t('alarms.title')}>
              {alarms.length === 0
                ? <div style={{ fontSize: 14 }}>{t('alarms.none')}</div>
                : (
                  <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13 }}>
                    {alarms.slice(0, 8).map(a => (
                      <li key={a.alarm_id}>
                        <Chip tone={a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'neutral'}>
                          {a.code}
                        </Chip>{' '}{a.message}
                      </li>
                    ))}
                  </ul>
                )}
            </Card>
          </div>

          <Card title={t('chart.title', { hours: WINDOW_HOURS })}>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {CHART_CHANNELS.map(channel => (
                <ChannelChart key={channel} channel={channel} label={t(`channels.${channel}`)}
                              points={telemetry.points} startMs={telemetry.startMs} endMs={telemetry.endMs} t={t} />
              ))}
            </div>
          </Card>

          <Card title={t('batches.title')}>
            {canRecord && <RecordBatchForm linkId={selected} onRecorded={loadBatches} t={t} />}
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['label', 'status', 'culture', 'started'].map(col => (
                      <th key={col} style={{ textAlign: 'start', padding: 6, borderBottom: '1px solid var(--c-border)' }}>
                        {t(`batches.${col}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {batches.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: 8 }}>{t('batches.none')}</td></tr>
                  )}
                  {batches.map(b => (
                    <tr key={b.batch_id}>
                      <td style={{ padding: 6 }}>{b.label}</td>
                      <td style={{ padding: 6 }}><Chip tone={b.status === 'running' ? 'good' : 'neutral'}>{t(`batches.statuses.${b.status}`)}</Chip></td>
                      <td style={{ padding: 6 }} dir="ltr">{b.lineage?.inoculum_code || '—'}</td>
                      <td style={{ padding: 6 }}>{b.started_at ? new Date(b.started_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {!desktopOnly && allLinks && (
        <LinkedSystemsPanel links={allLinks} canPair={isManager} onChanged={loadLinks}
          defaultKind={links?.length ? 'mycelium' : 'mabp'} />
      )}
    </div>
  );
}
